import { _ } from "../../../../core/polyglot/polyglot.js";
import { agencyPrintLogoToDefinition } from "../../../../core/helpers/pdf-util.js";
import { formatAsDashedYearMonthDay } from "../../../../core/helpers/date-formatting-util.js";
import { isString } from "../../../../core/helpers/string-util.js";
import DOMExporter from "../../../../core/pdfExport/DOMExporter.js";
import ImageAction from "../../../../models/wysiwygEditor/actions/ImageAction.js";
import PeriodReportSectionPDFExporter from "./PeriodReportSectionPDFExporter.js";
import ReportDisclaimerPDFExporter from "../../../../core/pdfExport/ReportDisclaimerPDFExporter.js";

const imageStyle = "image";
const pageMarginHorizontal = 20;
const pageMarginVertical = 40;
const headingMarginSpacing = 5;
const headingMarginContent = 10;
const imageFitSize = 300;
const maxCommentLines = 11;

/**
 * Export a period report into a PDF structure to be used by PDFMake to generate
 * a PDF file.
 *
 * @param {periodReport} spec.periodReport - Period report to export.
 */
export default class PeriodReportPDFExporter {
	constructor({ periodReport, reportDisclaimerTemplateVariables }) {
		this._periodReport = periodReport;

		this._reportDisclaimerExporter = new ReportDisclaimerPDFExporter({
			reportDisclaimerTemplateVariables,
			company: periodReport.getCompany(),
		});
	}

	/**
	 * Export the period report to a PDFMake structure and evaluate `callback`
	 * with that structure.
	 */
	async export() {
		const logo = await this._exportAgencyPrintLogo();
		const headingData = this._exportHeading();
		const commentData = this._exportPeriodReportComment();
		const disclaimerContent =
			this._reportDisclaimerExporter.exportReportDisclaimer();

		const sectionsData = await this._exportPeriodReportSections();

		const sectionsDataWithPageBreaks = this._addPageBreaks({
			sectionsData,
			commentData,
		});

		return {
			pageSize: "A4",
			title: this._periodReport.getTitle().toString(),
			pageMargins: [pageMarginHorizontal, pageMarginVertical],
			content: [
				logo,
				...headingData.content,
				...commentData.content,
				disclaimerContent,
				...sectionsDataWithPageBreaks.content,
			],
			styles: {
				...headingData.styles,
				...commentData.styles,
				...sectionsDataWithPageBreaks.styles,
				...this._reportDisclaimerExporter.getStyles(),
				[imageStyle]: {
					alignment: "center",
				},
			},
			images: {
				...headingData.images,
				...commentData.images,
				...sectionsDataWithPageBreaks.images,
			},
		};
	}

	_exportHeading() {
		return {
			content: [
				{
					text: this._periodReport
						.getTitle()
						.toString()
						.toUpperCase(),
					style: "title",
				},
				{
					text: this._periodReport
						.getSubtitle()
						.toString()
						.toUpperCase(),
					style: "subtitle",
				},
				this._makeAuthor(),
				{
					text: _("Published date: {date}", {
						date: formatAsDashedYearMonthDay(
							this._periodReport.getPublishDate(),
						),
					}).toString(),
					style: "publication",
				},
				{ text: "", style: "margin" },
			],
			styles: {
				title: {
					fontSize: 22,
					bold: true,
					color: "#222222",
					alignment: "center",
				},
				subtitle: {
					fontSize: 16,
					color: "#222222",
					margin: [0, headingMarginSpacing],
					alignment: "center",
				},
				publication: {
					fontSize: 12,
					color: "#222222",
					margin: [0, headingMarginSpacing],
					alignment: "center",
				},
				margin: { margin: [0, headingMarginContent] },
			},
		};
	}

	_makeAuthor() {
		const author = this._periodReport.getAuthor();
		if (!author || author.isUnknownUser() || author.isUnnamed()) {
			return {};
		}

		return {
			text: _("Author: {name}", { name: author.getName() }).toString(),
			style: "publication",
		};
	}

	_exportPeriodReportComment() {
		const exporter = new DOMExporter();

		const result = exporter.exportFromHTML(
			this._convertCommentSource(this._periodReport.getComment()),
		);

		if (!this._shouldShowPeriodReportComment(result)) {
			return { content: [] };
		}

		const images = this._extractImagesFromContent(result.content);

		return {
			content: [
				{
					layout: {
						hLineWidth: () => 1,
						vLineWidth: () => 0,
						hLineColor: () => "#d0d0d0",
						paddingLeft: () => headingMarginContent,
						paddingRight: () => headingMarginContent,
						fillColor: () => "#fffaf1",
					},
					table: {
						headerRows: 0,
						widths: ["*"],
						body: [[result.content]],
					},
				},
			],
			styles: result.styles,
			images,
		};
	}

	/**
	 * Iterate over the `image` node to extract the source, as pdfmake handles
	 * pictures using a URL as `src` this way, and this way only (see {@link https://pdfmake.github.io/docs/0.1/document-definition-object/images/}).
	 *
	 * No need to go recursive as the images from ImageAction are top nodes by
	 * construction.
	 *
	 * @param {object[]} content - Content of the PDF, already extracted
	 * @return {{[key: string]: string}} Image mapping
	 */
	_extractImagesFromContent(content) {
		const result = {};

		content.forEach((node) => {
			if (!node.image) {
				return;
			}

			const key = this._nextImageName();
			result[key] = node.image;
			node.image = key;
			node.style = imageStyle;
			node.fit = [imageFitSize, imageFitSize];
		});

		return result;
	}

	_nextImageName() {
		this._imageNameCounter ??= 0;
		return `image${this._imageNameCounter++}`;
	}

	_convertCommentSource(source) {
		return ImageAction.convertSourceToPDFExporter(source);
	}

	_exportPeriodReportSections() {
		return new PeriodReportSectionPDFExporter().export(
			this._periodReport.getSections(),
		);
	}

	_exportAgencyPrintLogo() {
		const imgNode = document.querySelector(".report-agency-logo img");

		if (!imgNode) return Promise.resolve("");

		return agencyPrintLogoToDefinition(imgNode && imgNode.src);
	}

	_addPageBreaks({ sectionsData, commentData }) {
		this._addPageBreakBeforeFirstSection({ sectionsData, commentData });
		this._addPageBreakBetweenSections(sectionsData);

		return sectionsData;
	}

	_addPageBreakBeforeFirstSection({ sectionsData, commentData }) {
		const firstSectionContent = sectionsData.content[0];
		if (
			!firstSectionContent ||
			this._isSectionEmpty(firstSectionContent) ||
			this._isCommentEmptyOrShort(commentData)
		)
			return;

		firstSectionContent[0] = {
			...firstSectionContent[0],
			pageBreak: "before",
		};
	}

	_addPageBreakBetweenSections(sectionsData) {
		sectionsData.content.slice(1).forEach((content) => {
			if (this._isSectionEmpty(content)) return;
			content[0] = { ...content[0], pageBreak: "before" };
		});
	}

	_isSectionEmpty(content) {
		return !content.length;
	}

	_shouldShowPeriodReportComment(commentData) {
		// If there are more than 1 block, we consider we want to
		// render the whole comment:
		if (commentData.content.length > 1) return true;

		// If there is nothing to show, don't show the comment:
		if (commentData.content.length === 0) return false;

		const onlyBlock = commentData.content[0];

		// If the only block is not a paragraph, show it in all cases:
		if (!onlyBlock.text) return true;

		// Show the only paragraph unless it is empty:
		return onlyBlock.text.length > 0;
	}

	_isCommentEmptyOrShort(commentData) {
		if (!commentData.content.length) return true;

		const commentDataTextTable = commentData.content[0].table.body[0][0];

		if (commentDataTextTable.length < 1) return true;

		const averageLineLength = 97;

		const commentLineNumber = commentDataTextTable.reduce(
			(acc, entry) =>
				acc +
				this._getDomEntryNumberOfLines({ entry, averageLineLength }),
			commentDataTextTable.length,
		);

		return commentLineNumber < maxCommentLines;
	}

	_getDomEntryNumberOfLines({ entry, averageLineLength }) {
		const entryContent = entry.text?.[0];

		if (!entryContent) return 0;

		const entryLength = isString(entryContent)
			? entryContent.length
			: entryContent.text?.length;

		if (!entryLength) return 0;

		return Math.ceil(entryLength / averageLineLength);
	}
}
